import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, PageHeader, StatusPill, Btn, Field, inputCls, Empty } from "../../components/dash/Kit";
import { api, errMessage } from "../../lib/api";

export default function AdminRevisions() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("open");
  const [notes, setNotes] = useState({});

  const load = (status) =>
    api
      .get("/admin/revisions", { params: status ? { status } : {} })
      .then((r) => setRows(r.data))
      .catch(() => {});

  useEffect(() => { load(filter); }, [filter]);

  const update = async (id, status) => {
    try {
      await api.put(`/admin/revisions/${id}`, { status, admin_notes: notes[id] || "" });
      toast.success(status === "completed" ? "Marked completed and customer notified." : "Revision updated.");
      load(filter);
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-revisions-page">
      <PageHeader
        title="Revision Requests"
        body="Open each request, review the original content and the customer's comments, then mark it completed."
        action={
          <select data-testid="revision-filter-select" className={`${inputCls} sm:w-52`} value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="completed">Completed</option>
          </select>
        }
      />

      {rows.length === 0 ? (
        <Empty testId="admin-revisions-empty" title="No revision requests" />
      ) : (
        <div className="space-y-4" data-testid="admin-revisions-list">
          {rows.map((r) => (
            <Card key={r.id} testId={`admin-revision-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-display text-base font-semibold text-slate-900">
                    {r.customer?.business_name || "Customer"}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Requested {new Date(r.created_at).toLocaleString()} · Areas: {r.areas.join(", ") || "—"}
                  </p>
                </div>
                <StatusPill status={r.status} />
              </div>

              <p className="mt-4 rounded-xl bg-slate-50 p-4 text-sm text-slate-700">{r.description}</p>

              {r.content_item && (
                <div className="mt-4 rounded-xl border border-slate-200 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Original content</p>
                  <p className="mt-1 text-sm font-semibold text-slate-900">
                    {r.content_item.post_date} · {r.content_item.platform} · {r.content_item.content_type}
                  </p>
                  <p className="mt-2 whitespace-pre-line text-sm text-slate-600">{r.content_item.caption}</p>
                </div>
              )}

              <div className="mt-4">
                <Field label="Admin notes to the customer" htmlFor={`rev-note-${r.id}`}>
                  <textarea
                    id={`rev-note-${r.id}`}
                    rows={2}
                    data-testid={`revision-note-input-${r.id}`}
                    className={inputCls}
                    defaultValue={r.admin_notes}
                    onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                  />
                </Field>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <Btn variant="outline" testId={`revision-in-progress-${r.id}`} onClick={() => update(r.id, "in_progress")}>
                  Mark in progress
                </Btn>
                <Btn testId={`revision-complete-${r.id}`} onClick={() => update(r.id, "completed")}>
                  Mark completed &amp; notify
                </Btn>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
